import React, { useState, useEffect } from "react";
import {
  Volume2,
  Save,
  Loader,
  AlertTriangle,
  CheckCircle,
  Bot,
  Mic,
} from "lucide-react";
import {
  getTTSVoices,
  updateDefaultTTSVoice,
  getAllVoiceProviders,
  switchSTTProvider,
  switchTTSProvider,
  deleteVoiceProvider,
  getInterviewProvider,
  updateInterviewProvider,
  getGeminiLiveOptions,
  getAvatarCompatibility,
  updateAvatarMode,
  getInterviewMode,
  updateInterviewMode,
} from "../../services/settingsapi";
import ConfirmationDialog from "../ConfirmationDialog";
import { SettingsSection, SelectField } from "../SettingsUIComponents";
import openaiLogo from "../../assets/SystemSettingSand/openai-icon.svg";
import googleLogo from "../../assets/SystemSettingSand/Google-plus-logo.svg";
import anthropicLogo from "../../assets/SystemSettingSand/anthropic-icon.svg";
import metaLogo from "../../assets/SystemSettingSand/mete-icon.svg";
import mistralLogo from "../../assets/SystemSettingSand/mistral-icon.svg";
import cohereLogo from "../../assets/SystemSettingSand/cohere-icon.svg";
import groqLogo from "../../assets/SystemSettingSand/groq-icon.svg";
import assemblyAiLogo from "../../assets/SystemSettingSand/assemblyai-icon.svg";
import azureLogo from "../../assets/SystemSettingSand/azure-icon.svg";

const PROVIDER_LOGOS = {
  openai: openaiLogo,
  google: googleLogo,
  gemini: googleLogo,
  anthropic: anthropicLogo,
  meta: metaLogo,
  mistral: mistralLogo,
  cohere: cohereLogo,
  groq: groqLogo,
  assemblyai: assemblyAiLogo,
  azure: azureLogo,
};

const ProviderMark = ({ provider, type }) => {
  const providerKey = String(provider || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const logo = PROVIDER_LOGOS[providerKey];
  const FallbackIcon = type === "stt" ? Mic : Volume2;

  return (
    <span className="settings-tts-provider-mark" aria-hidden="true">
      {logo ? <img src={logo} alt="" /> : <FallbackIcon />}
    </span>
  );
};

const VoiceProviderSettings = ({
  canEditSettings,
  canViewSettings,
  canDeleteSettings,
}) => {
  const notifyCapabilityRefresh = () => {
    window.dispatchEvent(new Event("interview-provider-config-changed"));
  };
  // Interview Provider Settings
  const [interviewProvider, setInterviewProvider] = useState("legacy");
  const [interviewProviderLoading, setInterviewProviderLoading] = useState(false);
  const [interviewProviderMessage, setInterviewProviderMessage] = useState({ type: "", text: "" });
  const [geminiLiveConfig, setGeminiLiveConfig] = useState({ voice: "Aoede", language: "English", gender: "any", tone: "professional", style: "interviewer", pace: "normal", instructions: "" });
  const [geminiLiveOptions, setGeminiLiveOptions] = useState({ voices: [], languages: [], genders: ["any"], tones: [], styles: [], paces: [] });
  const [geminiLiveLoading, setGeminiLiveLoading] = useState(false);
  const [avatarCompatibility, setAvatarCompatibility] = useState(null);

  // Interview Mode Settings
  const [interviewMode, setInterviewMode] = useState("planned");
  const [followUpGenerationPercentage, setFollowUpGenerationPercentage] = useState(25);
  const [averageQuestionTimeSeconds, setAverageQuestionTimeSeconds] = useState(45);
  const [questionPoolMultiplier, setQuestionPoolMultiplier] = useState(2);
  const [plannedSoftExtensionMinutes, setPlannedSoftExtensionMinutes] = useState(4);
  const [interviewModeLoading, setInterviewModeLoading] = useState(false);
  const [interviewModeMessage, setInterviewModeMessage] = useState({ type: "", text: "" });

  // TTS Voices Settings
  const [ttsVoices, setTtsVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsMessage, setTtsMessage] = useState({ type: "", text: "" });
  const [voiceDetails, setVoiceDetails] = useState({
    name: "",
    gender: "",
    accent: "",
    type: "",
  });

  // Voice Providers Settings
  const [allVoiceProviders, setAllVoiceProviders] = useState([]);
  const [sttProviders, setSttProviders] = useState([]);
  const [ttsProviders, setTtsProviders] = useState([]);
  const [activeSttProvider, setActiveSttProvider] = useState(null);
  const [activeTtsProvider, setActiveTtsProvider] = useState(null);
  const [voiceProvidersLoading, setVoiceProvidersLoading] = useState(false);
  const [voiceProvidersMessage, setVoiceProvidersMessage] = useState({
    type: "",
    text: "",
  });

  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  // Fetch TTS voices, providers, and interview provider on component mount
  useEffect(() => {
    if (!canViewSettings) return;

    const fetchTTSVoices = async () => {
      try {
        setTtsLoading(true);
        const data = await getTTSVoices();
        if (data.voices && Array.isArray(data.voices)) {
          setTtsVoices(data.voices);
          const defaultVoiceName =
            data.default_voice ||
            (data.voices.length > 0 ? data.voices[0].name : null);

          if (defaultVoiceName) {
            setSelectedVoice(defaultVoiceName);
            const defaultVoiceObj = data.voices.find(
              (v) => v.name === defaultVoiceName,
            );
            if (defaultVoiceObj) {
              setVoiceDetails({
                name: defaultVoiceObj.name,
                gender: defaultVoiceObj.gender,
                accent: defaultVoiceObj.accent,
                type: defaultVoiceObj.type,
              });
            }
          }
        }
        setTtsMessage({ type: "", text: "" });
      } catch (error) {
        console.error("Error fetching TTS voices:", error);
        setTtsMessage({
          type: "error",
          text: "Error loading TTS voices: " + error.message,
        });
      } finally {
        setTtsLoading(false);
      }
    };

    const fetchVoiceProviders = async () => {
      try {
        setVoiceProvidersLoading(true);
        const data = await getAllVoiceProviders();
        setAllVoiceProviders(data);

        const stt = data.filter((p) => p.type === "stt");
        const tts = data.filter((p) => p.type === "tts");
        setSttProviders(stt);
        setTtsProviders(tts);

        const activeStt = stt.find((p) => p.active);
        const activeTts = tts.find((p) => p.active);
        setActiveSttProvider(activeStt);
        setActiveTtsProvider(activeTts);

        setVoiceProvidersMessage({ type: "", text: "" });
      } catch (error) {
        console.error("Error fetching voice providers:", error);
        setVoiceProvidersMessage({
          type: "error",
          text: "Error loading voice providers: " + error.message,
        });
      } finally {
        setVoiceProvidersLoading(false);
      }
    };

    const fetchInterviewProvider = async () => {
      try {
        const data = await getInterviewProvider();
        setInterviewProvider(data.provider);
        if (data.gemini_live) setGeminiLiveConfig((current) => ({ ...current, ...data.gemini_live }));
        await refreshAvatarCompatibility(false);
        setInterviewProviderMessage({ type: "", text: "" });
      } catch (error) {
        console.error("Error fetching interview provider:", error);
        setInterviewProviderMessage({
          type: "error",
          text: "Error loading interview provider: " + error.message,
        });
      }
    };

    const fetchInterviewMode = async () => {
      try {
        const data = await getInterviewMode();
        setInterviewMode(data.mode);
        setFollowUpGenerationPercentage(data.follow_up_generation_percentage ?? 25);
        setAverageQuestionTimeSeconds(data.average_question_time_seconds ?? 45);
        setQuestionPoolMultiplier(data.question_pool_multiplier ?? 2);
        setPlannedSoftExtensionMinutes(data.planned_soft_extension_minutes ?? 4);
        setInterviewModeMessage({ type: "", text: "" });
      } catch (error) {
        console.error("Error fetching interview mode:", error);
        setInterviewModeMessage({
          type: "error",
          text: "Error loading interview mode: " + error.message,
        });
      }
    };

    const fetchGeminiLiveOptions = async () => {
      try {
        const data = await getGeminiLiveOptions();
        setGeminiLiveOptions(data);
        setGeminiLiveConfig((current) => {
          const selectedVoice = data.voices?.find((voice) => voice.id === current.voice);
          return selectedVoice && current.gender === "any"
            ? { ...current, gender: selectedVoice.gender }
            : current;
        });
      } catch (error) {
        console.error("Error fetching Gemini Live options:", error);
        setInterviewProviderMessage({ type: "error", text: "Error loading Gemini Live voice options: " + error.message });
      }
    };

    fetchTTSVoices();
    fetchVoiceProviders();
    fetchInterviewProvider();
    fetchInterviewMode();
    fetchGeminiLiveOptions();
  }, [canViewSettings]);

  // Handle voice selection change
  const handleVoiceChange = (e) => {
    const voiceName = e.target.value;
    const selectedVoiceObj = ttsVoices.find((v) => v.name === voiceName);

    if (selectedVoiceObj) {
      setSelectedVoice(voiceName);
      setVoiceDetails({
        name: selectedVoiceObj.name,
        gender: selectedVoiceObj.gender,
        accent: selectedVoiceObj.accent,
        type: selectedVoiceObj.type,
      });
    }
  };

  // Save TTS voice selection
  const saveTTSVoice = async () => {
    if (!selectedVoice) {
      setTtsMessage({
        type: "error",
        text: "Please select a voice",
      });
      return;
    }

    if (!canEditSettings) {
      setTtsMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: "Confirm TTS Voice Selection",
      message: `Are you sure you want to set "${selectedVoice}" as the default TTS voice? This will be used for all text-to-speech interactions.`,
      onConfirm: async () => {
        try {
          setTtsLoading(true);
          await updateDefaultTTSVoice(selectedVoice);
          setTtsMessage({
            type: "success",
            text: `TTS voice "${selectedVoice}" has been set as default!`,
          });
          await refreshAvatarCompatibility(true);

          setTimeout(() => setTtsMessage({ type: "", text: "" }), 5000);
        } catch (error) {
          console.error("Error saving TTS voice:", error);
          setTtsMessage({
            type: "error",
            text: "Error saving TTS voice: " + error.message,
          });
        } finally {
          setTtsLoading(false);
        }
      },
    });
  };

  // Handle STT provider switch
  const handleSwitchSTTProvider = async (providerName) => {
    if (!canEditSettings) {
      setVoiceProvidersMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: "Switch STT Provider",
      message: `Are you sure you want to switch the STT provider to "${providerName}"? This will affect speech recognition during interviews.`,
      onConfirm: async () => {
        try {
          setVoiceProvidersLoading(true);
          await switchSTTProvider(providerName);

          const updated = allVoiceProviders.map((p) => ({
            ...p,
            active:
              p.provider === providerName && p.type === "stt"
                ? true
                : p.type === "stt"
                  ? false
                  : p.active,
          }));
          setAllVoiceProviders(updated);
          const newActiveStt = updated.find(
            (p) => p.type === "stt" && p.active,
          );
          setActiveSttProvider(newActiveStt);

          setVoiceProvidersMessage({
            type: "success",
            text: `STT provider switched to "${providerName}" successfully!`,
          });
          if (interviewProvider === "legacy") await refreshAvatarCompatibility(false);
          notifyCapabilityRefresh();

          setTimeout(
            () => setVoiceProvidersMessage({ type: "", text: "" }),
            5000,
          );
        } catch (error) {
          console.error("Error switching STT provider:", error);
          setVoiceProvidersMessage({
            type: "error",
            text: "Error switching STT provider: " + error.message,
          });
        } finally {
          setVoiceProvidersLoading(false);
        }
      },
    });
  };

  // Handle TTS provider switch
  const handleSwitchTTSProvider = async (providerName) => {
    if (!canEditSettings) {
      setVoiceProvidersMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: "Switch TTS Provider",
      message: `Are you sure you want to switch the TTS provider to "${providerName}"? This will affect voice output during interviews.`,
      onConfirm: async () => {
        try {
          setVoiceProvidersLoading(true);
          await switchTTSProvider(providerName);

          const updated = allVoiceProviders.map((p) => ({
            ...p,
            active:
              p.provider === providerName && p.type === "tts"
                ? true
                : p.type === "tts"
                  ? false
                  : p.active,
          }));
          setAllVoiceProviders(updated);
          const newActiveTts = updated.find(
            (p) => p.type === "tts" && p.active,
          );
          setActiveTtsProvider(newActiveTts);

          setVoiceProvidersMessage({
            type: "success",
            text: `TTS provider switched to "${providerName}" successfully!`,
          });
          if (interviewProvider === "legacy") await refreshAvatarCompatibility(providerName.toLowerCase() === "openai");
          notifyCapabilityRefresh();

          setTimeout(
            () => setVoiceProvidersMessage({ type: "", text: "" }),
            5000,
          );
        } catch (error) {
          console.error("Error switching TTS provider:", error);
          setVoiceProvidersMessage({
            type: "error",
            text: "Error switching TTS provider: " + error.message,
          });
        } finally {
          setVoiceProvidersLoading(false);
        }
      },
    });
  };

  // Handle delete provider
  const handleDeleteProvider = async (providerName) => {
    if (!canDeleteSettings) {
      setVoiceProvidersMessage({
        type: "error",
        text: "You do not have permission to delete settings",
      });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: "Delete Provider",
      message: `Are you sure you want to delete the provider "${providerName}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          setVoiceProvidersLoading(true);
          await deleteVoiceProvider(providerName);

          const updated = allVoiceProviders.filter(
            (p) => p.provider !== providerName,
          );
          setAllVoiceProviders(updated);
          const stt = updated.filter((p) => p.type === "stt");
          const tts = updated.filter((p) => p.type === "tts");
          setSttProviders(stt);
          setTtsProviders(tts);

          setVoiceProvidersMessage({
            type: "success",
            text: `Provider "${providerName}" deleted successfully!`,
          });

          setTimeout(
            () => setVoiceProvidersMessage({ type: "", text: "" }),
            5000,
          );
        } catch (error) {
          console.error("Error deleting provider:", error);
          setVoiceProvidersMessage({
            type: "error",
            text: "Error deleting provider: " + error.message,
          });
        } finally {
          setVoiceProvidersLoading(false);
        }
      },
    });
  };

  const formatLabel = (text) => {
    if (!text) return "";
    return text
      .replace(/_/g, " ") // whisper_local → whisper local
      .replace(/\b\w/g, (char) => char.toUpperCase()); // capitalize each word
  };

  const handleUpdateInterviewProvider = async (newProvider) => {
    if (!canEditSettings) {
      setInterviewProviderMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    try {
      setInterviewProviderLoading(true);
      const saved = await updateInterviewProvider({ provider: newProvider });
      setInterviewProvider(newProvider);
      if (saved.gemini_live) setGeminiLiveConfig((current) => ({ ...current, ...saved.gemini_live }));
      if (newProvider === "gemini_live" || newProvider === "legacy") await refreshAvatarCompatibility(true);
      notifyCapabilityRefresh();
      setInterviewProviderMessage({
        type: "success",
        text: "Interview provider updated successfully",
      });
      setTimeout(() => setInterviewProviderMessage({ type: "", text: "" }), 5000);
    } catch (error) {
      console.error("Error updating interview provider:", error);
      setInterviewProviderMessage({
        type: "error",
        text: "Error updating interview provider: " + error.message,
      });
    } finally {
      setInterviewProviderLoading(false);
    }
  };

  const saveGeminiLiveSettings = async () => {
    if (!canEditSettings) return;
    try {
      setGeminiLiveLoading(true);
      const saved = await updateInterviewProvider({ provider: "gemini_live", gemini_live: geminiLiveConfig });
      setGeminiLiveConfig((current) => ({ ...current, ...saved.gemini_live }));
      setInterviewProvider("gemini_live");
      notifyCapabilityRefresh();
      await refreshAvatarCompatibility(true);
      setInterviewProviderMessage({ type: "success", text: "Gemini Live voice settings saved successfully" });
    } catch (error) {
      console.error("Error saving Gemini Live settings:", error);
      setInterviewProviderMessage({ type: "error", text: "Error saving Gemini Live settings: " + error.message });
    } finally {
      setGeminiLiveLoading(false);
    }
  };

  const refreshAvatarCompatibility = async (offerFix = false) => {
    try {
      const compatibility = await getAvatarCompatibility();
      setAvatarCompatibility(compatibility);
      if (offerFix && compatibility.compatible === false) {
        const avatarModeLabel = compatibility.avatar_mode === "2d" ? "2D avatar" : "static avatar";
        setConfirmationDialog({
          open: true,
          title: "Voice & Avatar Mismatch",
          message: `${compatibility.voice_source === "openai_tts" ? "OpenAI TTS" : "Gemini Live"} voice ${compatibility.voice} is ${compatibility.voice_gender}, but the current ${avatarModeLabel} is ${compatibility.avatar_gender}. Switch the avatar to ${compatibility.voice_gender}?`,
          onConfirm: async () => {
            await updateAvatarMode({ mode: compatibility.avatar_mode, gender: compatibility.voice_gender });
            setAvatarCompatibility((current) => ({ ...current, avatar_gender: compatibility.voice_gender, compatible: true }));
          },
        });
      }
    } catch (error) {
      console.warn("Could not load voice/avatar compatibility:", error);
    }
  };

  const handleGeminiVoiceChange = (event) => {
    const voice = geminiLiveOptions.voices.find((item) => item.id === event.target.value);
    if (!voice) return;
    // Voice profile defaults make the form behave like the existing OpenAI TTS
    // picker. Administrators can still adjust tone/style/pace afterwards.
    setGeminiLiveConfig((current) => ({
      ...current,
      voice: voice.id,
      gender: voice.gender,
      tone: voice.recommended_tone,
      style: voice.recommended_style,
      pace: voice.recommended_pace,
    }));
  };

  const handleUpdateInterviewMode = async (newMode) => {
    if (!canEditSettings) {
      setInterviewModeMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    try {
      setInterviewModeLoading(true);
      await updateInterviewMode({
        mode: newMode,
        follow_up_generation_percentage: followUpGenerationPercentage,
        average_question_time_seconds: averageQuestionTimeSeconds,
        question_pool_multiplier: questionPoolMultiplier,
        planned_soft_extension_minutes: plannedSoftExtensionMinutes,
      });
      setInterviewMode(newMode);
      setInterviewModeMessage({
        type: "success",
        text: "Interview mode updated successfully",
      });
      setTimeout(() => setInterviewModeMessage({ type: "", text: "" }), 5000);
    } catch (error) {
      console.error("Error updating interview mode:", error);
      setInterviewModeMessage({
        type: "error",
        text: "Error updating interview mode: " + error.message,
      });
    } finally {
      setInterviewModeLoading(false);
    }
  };

  const handleSavePlannedInterviewSettings = async () => {
    if (!canEditSettings) {
      setInterviewModeMessage({ type: "error", text: "You do not have permission to edit settings" });
      return;
    }
    const percentage = Number(followUpGenerationPercentage);
    const averageTime = Number(averageQuestionTimeSeconds);
    const poolMultiplier = Number(questionPoolMultiplier);
    const softExtension = Number(plannedSoftExtensionMinutes);
    if (!Number.isInteger(percentage) || percentage < 0 || percentage > 100 ||
      !Number.isInteger(averageTime) || averageTime < 1 || averageTime > 600 ||
      !Number.isInteger(poolMultiplier) || poolMultiplier < 1 || poolMultiplier > 10 ||
      !Number.isInteger(softExtension) || softExtension < 0 || softExtension > 15) {
      setInterviewModeMessage({ type: "error", text: "Enter valid Planned interview settings" });
      return;
    }
    try {
      setInterviewModeLoading(true);
      await updateInterviewMode({
        mode: interviewMode,
        follow_up_generation_percentage: percentage,
        average_question_time_seconds: averageTime,
        question_pool_multiplier: poolMultiplier,
        planned_soft_extension_minutes: softExtension,
      });
      setFollowUpGenerationPercentage(percentage);
      setAverageQuestionTimeSeconds(averageTime);
      setQuestionPoolMultiplier(poolMultiplier);
      setPlannedSoftExtensionMinutes(softExtension);
      setInterviewModeMessage({ type: "success", text: "Planned interview settings updated successfully" });
      setTimeout(() => setInterviewModeMessage({ type: "", text: "" }), 5000);
    } catch (error) {
      console.error("Error updating Planned interview settings:", error);
      setInterviewModeMessage({ type: "error", text: "Error updating Planned interview settings: " + error.message });
    } finally {
      setInterviewModeLoading(false);
    }
  };

  return (
    <div className="settings-content-tts">
      <SettingsSection
        icon={Bot}
        title="Interview Provider"
        description="Select the interview provider to use"
      >
        <div className="space-y-6">
          {interviewProviderMessage.text && (
            <div
              className={`p-4 border rounded-xl flex items-center gap-3 ${
                interviewProviderMessage.type === "success"
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              {interviewProviderMessage.type === "success" ? (
                <CheckCircle className={`w-5 h-5 text-green-600`} />
              ) : (
                <AlertTriangle className={`w-5 h-5 text-red-600`} />
              )}
              <p
                className={`text-sm font-medium ${interviewProviderMessage.type === "success" ? "text-green-700" : "text-red-700"}`}
              >
                {interviewProviderMessage.text}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { id: "legacy", label: "Legacy", description: "The original interview system" },
              { id: "gemini_live", label: "Gemini Live", description: "Google's Gemini Live API for interactive interviews" },
            ].map((provider) => (
              <div
                key={provider.id}
                className={`settings-tts-card settings-tts-option ${interviewProvider === provider.id ? "is-selected" : ""}` }
                onClick={() => handleUpdateInterviewProvider(provider.id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{provider.label}</p>
                    <p className="text-sm text-gray-600 mt-1">{provider.description}</p>
                  </div>
                  {interviewProvider === provider.id && (
                    <span className="settings-tts-pill">
                      Active
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {interviewProviderLoading && (
            <div className="flex items-center justify-center py-4">
              <Loader className="w-5 h-5 animate-spin text-[#4B1498]" />
              <span className="ml-2 text-sm text-gray-600">Updating...</span>
            </div>
          )}

          {interviewProvider === "gemini_live" && (
            <div className="settings-tts-gemini-panel">
              <div>
                <h3 className="font-semibold text-gray-900">Gemini Live Voice Configuration</h3>
                <p className="text-sm text-gray-600 mt-1">Voice is applied natively to the Live session. Language and delivery preferences are applied once as session instructions.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  ["Language", "language", geminiLiveOptions.languages],
                  ["Tone", "tone", geminiLiveOptions.tones],
                  ["Speaking style", "style", geminiLiveOptions.styles],
                  ["Speaking pace", "pace", geminiLiveOptions.paces],
                ].map(([label, key, options]) => (
                  <div key={key}>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">{label}</label>
                    <select value={geminiLiveConfig[key]} disabled={!canEditSettings || geminiLiveLoading} onChange={(event) => setGeminiLiveConfig((current) => ({ ...current, [key]: event.target.value }))} className="settings-tts-field w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 disabled:bg-gray-100 disabled:text-gray-500">
                      {options.map((option) => {
                        const value = typeof option === "string" ? option : option.value;
                        const text = typeof option === "string" ? option.replace(/\b\w/g, (character) => character.toUpperCase()) : option.label;
                        return <option key={value} value={value}>{text}</option>;
                      })}
                    </select>
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">Voice</label>
                  <select value={geminiLiveConfig.voice} disabled={!canEditSettings || geminiLiveLoading} onChange={handleGeminiVoiceChange} className="settings-tts-field w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 disabled:bg-gray-100 disabled:text-gray-500">
                    {geminiLiveOptions.voices.map((voice) => <option key={voice.id} value={voice.id}>{voice.name} — {voice.gender === "female" ? "Female" : "Male"} · {voice.description}</option>)}
                  </select>
                </div>
              </div>
              {(() => {
                const selectedGeminiVoice = geminiLiveOptions.voices.find((voice) => voice.id === geminiLiveConfig.voice);
                if (!selectedGeminiVoice) return null;
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      ["Voice name", selectedGeminiVoice.name],
                      ["Gender", selectedGeminiVoice.gender],
                      ["Voice character", selectedGeminiVoice.description],
                      ["Recommended delivery", `${selectedGeminiVoice.recommended_tone} · ${selectedGeminiVoice.recommended_style} · ${selectedGeminiVoice.recommended_pace}`],
                    ].map(([label, value]) => (
                      <div key={label} className="settings-tts-detail-tile">
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">{label}</label>
                        <div className="settings-tts-detail-value capitalize">{value}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Voice instructions <span className="font-normal text-gray-500">(optional)</span></label>
                <textarea value={geminiLiveConfig.instructions} maxLength={2000} disabled={!canEditSettings || geminiLiveLoading} onChange={(event) => setGeminiLiveConfig((current) => ({ ...current, instructions: event.target.value }))} rows={3} className="settings-tts-field w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 disabled:bg-gray-100 disabled:text-gray-500" placeholder="For example: use a warm, reassuring delivery." />
                <p className="text-xs text-gray-500 mt-2">Gender is a Viriqo Hires voice-presentation label to help with selection; Gemini Live receives only the selected voice name.</p>
              </div>
              <button onClick={saveGeminiLiveSettings} disabled={!canEditSettings || geminiLiveLoading} className="settings-tts-primary-btn flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed">
                {geminiLiveLoading ? <Loader className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                {geminiLiveLoading ? "Saving..." : "Save Gemini Live Settings"}
              </button>
            </div>
          )}
          {avatarCompatibility?.compatible === false && (
            <div className="p-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-900">
              <p className="font-semibold">⚠ Voice & Avatar mismatch</p>
              <p className="text-sm mt-1">{avatarCompatibility.voice_source === "openai_tts" ? "OpenAI TTS" : "Gemini Live"} voice: {avatarCompatibility.voice_gender}. {avatarCompatibility.avatar_mode === "2d" ? "2D avatar" : "Static avatar"}: {avatarCompatibility.avatar_gender}.</p>
              <button type="button" onClick={() => updateAvatarMode({ mode: avatarCompatibility.avatar_mode, gender: avatarCompatibility.voice_gender }).then(() => refreshAvatarCompatibility())} disabled={!canEditSettings} className="mt-3 text-sm font-semibold underline hover:no-underline">Switch avatar to {avatarCompatibility.voice_gender}</button>
            </div>
          )}
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Bot}
        title="Interview Mode"
        description="Select the interview mode to use"
      >
        <div className="space-y-6">
          {interviewModeMessage.text && (
            <div
              className={`p-4 border rounded-xl flex items-center gap-3 ${
                interviewModeMessage.type === "success"
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              {interviewModeMessage.type === "success" ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-600" />
              )}
              <p
                className={`text-sm font-medium ${interviewModeMessage.type === "success" ? "text-green-700" : "text-red-700"}`}
              >
                {interviewModeMessage.text}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { id: "planned", label: "Predefined", description: "Pre-generated interview questions" },
              { id: "dynamic", label: "Dynamic", description: "AI generates questions during the interview" },
            ].map((mode) => (
              <div
                key={mode.id}
                className={`settings-tts-card settings-tts-mode-card ${interviewMode === mode.id ? "is-selected" : "" }` }
                onClick={() => handleUpdateInterviewMode(mode.id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{mode.label}</p>
                    <p className="text-sm text-gray-600 mt-1">{mode.description}</p>
                  </div>
                  {interviewMode === mode.id && (
                    <span className="settings-tts-pill">
                      Active
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {interviewMode === "planned" && (
            <div className="max-w-xl border border-gray-200 rounded-xl p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block text-sm font-semibold text-gray-900" htmlFor="follow-up-generation-percentage">
                  Follow-up %
                  <input
                    id="follow-up-generation-percentage"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={followUpGenerationPercentage}
                    disabled={!canEditSettings || interviewModeLoading}
                    onChange={(event) => setFollowUpGenerationPercentage(event.target.value)}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:border-[var(--primary)] focus:ring-2 focus:ring-[rgba(166,111,255,0.16)] disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </label>
                <label className="block text-sm font-semibold text-gray-900" htmlFor="average-question-time-seconds">
                  Avg. question time (sec)
                  <input
                    id="average-question-time-seconds"
                    type="number"
                    min="1"
                    max="600"
                    step="1"
                    value={averageQuestionTimeSeconds}
                    disabled={!canEditSettings || interviewModeLoading}
                    onChange={(event) => setAverageQuestionTimeSeconds(event.target.value)}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:border-[var(--primary)] focus:ring-2 focus:ring-[rgba(166,111,255,0.16)] disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </label>
                <label className="block text-sm font-semibold text-gray-900" htmlFor="question-pool-multiplier">
                  Question pool multiplier
                  <input
                    id="question-pool-multiplier"
                    type="number"
                    min="1"
                    max="10"
                    step="1"
                    value={questionPoolMultiplier}
                    disabled={!canEditSettings || interviewModeLoading}
                    onChange={(event) => setQuestionPoolMultiplier(event.target.value)}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:border-[var(--primary)] focus:ring-2 focus:ring-[rgba(166,111,255,0.16)] disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </label>
                <label className="block text-sm font-semibold text-gray-900" htmlFor="planned-soft-extension-minutes">
                  Soft extension (min)
                  <input
                    id="planned-soft-extension-minutes"
                    type="number"
                    min="0"
                    max="15"
                    step="1"
                    value={plannedSoftExtensionMinutes}
                    disabled={!canEditSettings || interviewModeLoading}
                    onChange={(event) => setPlannedSoftExtensionMinutes(event.target.value)}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:border-[var(--primary)] focus:ring-2 focus:ring-[rgba(166,111,255,0.16)] disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </label>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSavePlannedInterviewSettings}
                  disabled={!canEditSettings || interviewModeLoading}
                  className="settings-tts-primary-btn flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            </div>
          )}

          {interviewModeLoading && (
            <div className="flex items-center justify-center py-4">
              <Loader className="w-5 h-5 animate-spin text-[#4B1498]" />
              <span className="ml-2 text-sm text-gray-600">Updating...</span>
            </div>
          )}
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Volume2}
        title="Voice Providers Configuration"
        description="Manage STT and TTS voice providers for interviews"
      >
        <div className="space-y-6">
          {voiceProvidersMessage.text && (
            <div
              className={`p-4 border rounded-xl flex items-center gap-3 ${
                voiceProvidersMessage.type === "success"
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              {voiceProvidersMessage.type === "success" ? (
                <CheckCircle className={`w-5 h-5 text-green-600`} />
              ) : (
                <AlertTriangle className={`w-5 h-5 text-red-600`} />
              )}
              <p
                className={`text-sm font-medium ${voiceProvidersMessage.type === "success" ? "text-green-700" : "text-red-700"}`}
              >
                {voiceProvidersMessage.text}
              </p>
            </div>
          )}

          {/* STT Providers */}
          <div>
            <h3 className="text-lg font-bold text-[var(--text-primary)] mb-5">
              Voice Providers Configuration
            </h3>
            {voiceProvidersLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="w-6 h-6 animate-spin text-[#4B1498]" />
              </div>
            ) : sttProviders.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sttProviders.map((provider) => (
                  <div
                    key={provider.provider}
                    className={`settings-tts-card settings-tts-provider-card ${provider.active ? "is-active" : ""}` }
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <ProviderMark provider={provider.provider} type="stt" />
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900">
                            {formatLabel(provider.provider)}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            Active: {provider.active ? "Yes" : "No"}
                          </p>
                        </div>
                      </div>
                      {provider.active ? (
                        <span className="settings-tts-pill">
                          Active
                        </span>
                      ) : (
                        <button
                          onClick={() =>
                            handleSwitchSTTProvider(provider.provider)
                          }
                          disabled={voiceProvidersLoading || !canEditSettings}
                          className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                            canEditSettings && !voiceProvidersLoading
                              ? "settings-tts-activate-btn"
                              : "bg-gray-300 text-gray-500 cursor-not-allowed"
                          }`}
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-sm">
                No STT providers configured
              </p>
            )}
          </div>

          <div className="border-t border-gray-300 pt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-5">
              Text-to-Speech (TTS) Providers
            </h3>
            {voiceProvidersLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="w-6 h-6 animate-spin text-[#4B1498]" />
              </div>
            ) : ttsProviders.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ttsProviders.map((provider) => (
                  <div
                    key={provider.provider}
                    className={`settings-tts-card settings-tts-provider-card ${provider.active ? "is-active" : ""}` }
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <ProviderMark provider={provider.provider} type="tts" />
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900">
                            {formatLabel(provider.provider)}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            Active: {provider.active ? "Yes" : "No"}
                          </p>
                        </div>
                      </div>
                      {provider.active ? (
                        <span className="settings-tts-pill">
                          Active
                        </span>
                      ) : (
                        <button
                          onClick={() =>
                            handleSwitchTTSProvider(provider.provider)
                          }
                          disabled={voiceProvidersLoading || !canEditSettings}
                          className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                            canEditSettings && !voiceProvidersLoading
                              ? "settings-tts-activate-btn"
                              : "bg-gray-300 text-gray-500 cursor-not-allowed"
                          }`}
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-sm">
                No TTS providers configured
              </p>
            )}
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Volume2}
        title="Default TTS Voice Selection"
        description="Choose the default voice for text-to-speech"
      >
        <div className="space-y-6">
          {ttsMessage.text && (
            <div
              className={`p-4 border rounded-xl flex items-center gap-3 ${
                ttsMessage.type === "success"
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              {ttsMessage.type === "success" ? (
                <CheckCircle className={`w-5 h-5 text-green-600`} />
              ) : (
                <AlertTriangle className={`w-5 h-5 text-red-600`} />
              )}
              <p
                className={`text-sm font-medium ${ttsMessage.type === "success" ? "text-green-700" : "text-red-700"}`}
              >
                {ttsMessage.text}
              </p>
            </div>
          )}

          <div className="space-y-6">
            <SelectField
              label="Select Default Voice"
              value={selectedVoice || ""}
              onChange={handleVoiceChange}
              options={ttsVoices.map((voice) => ({
                value: voice.name,
                label: voice.name.charAt(0).toUpperCase() + voice.name.slice(1),
              }))}
              helpText="Choose the default TTS voice from OpenAI's available voices"
              canEdit={canEditSettings}
            />

            {selectedVoice && voiceDetails && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-[var(--primary-light)] border border-[var(--border-color)] rounded-lg">
                  <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-2">
                    Voice Name
                  </label>
                  <div className="text-lg font-bold text-[var(--primary)]">
                    {formatLabel(voiceDetails.name)}
                  </div>{" "}
                </div>
                <div className="p-4 bg-[var(--primary-light)] border border-[var(--border-color)] rounded-lg">
                  <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-2">
                    Gender
                  </label>
                  <div className="text-lg font-bold text-[var(--primary)] capitalize">
                    {voiceDetails.gender}
                  </div>
                </div>
                <div className="p-4 bg-[var(--primary-light)] border border-[var(--border-color)] rounded-lg">
                  <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-2">
                    Accent
                  </label>
                  <div className="text-lg font-bold text-[var(--primary)] capitalize">
                    {voiceDetails.accent}
                  </div>
                </div>
                <div className="p-4 bg-[var(--primary-light)] border border-[var(--border-color)] rounded-lg">
                  <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-2">
                    Type
                  </label>
                  <div className="text-lg font-bold text-[var(--primary)] capitalize">
                    {voiceDetails.type}
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={saveTTSVoice}
              disabled={!canEditSettings || ttsLoading || !selectedVoice}
              className={`w-full px-6 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                canEditSettings && !ttsLoading && selectedVoice
                  ? "bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] text-white hover:shadow-lg"
                  : "bg-[var(--border-color)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
              }`}
              title={
                !canEditSettings
                  ? "You do not have permission to edit settings"
                  : "Select a voice first"
              }
            >
              {ttsLoading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Default TTS Voice
                </>
              )}
            </button>
          </div>
        </div>
      </SettingsSection>

      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={() =>
          setConfirmationDialog({ ...confirmationDialog, open: false })
        }
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText="Confirm"
        cancelText="Cancel"
        isDangerous={false}
      />
    </div>
  );
};

export default VoiceProviderSettings;
